import { z } from 'zod';

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a digit');

export const registerSchema = z.object({
  email: z.string().email(),
  password: passwordSchema,
  name: z.string().min(1).max(120),
  organizationName: z.string().min(1).max(120).optional(),
  timezone: z.string().min(1).max(64).default('UTC'),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: z.string().email() });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const verifyEmailSchema = z.object({ token: z.string().min(1) });
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const refreshTokenSchema = z.object({ refreshToken: z.string().min(1).optional() });
export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;

export const oauthExchangeSchema = z.object({ code: z.string().min(1) });
export type OAuthExchangeInput = z.infer<typeof oauthExchangeSchema>;

// Long-lived credential for the CLI and the MCP server. `expiresAt` is
// optional because the common case — a token pasted into an MCP host's
// config — has no natural end date; leaving it null means "until revoked".
export const createApiTokenSchema = z.object({
  name: z.string().min(1).max(80),
  expiresAt: z.string().datetime().nullable().optional(),
});
export type CreateApiTokenInput = z.infer<typeof createApiTokenSchema>;

// Browser login for the CLI (RFC 8628-style device grant, see the API's
// CliAuthService). `clientLabel` is the CLI's own description of where the
// request came from — shown on the approval screen so the person approving can
// tell it apart from a request they didn't start.
export const cliAuthStartSchema = z.object({
  clientLabel: z.string().min(1).max(120).optional(),
});
export type CliAuthStartInput = z.infer<typeof cliAuthStartSchema>;

// Re-sent by the browser on approval, and checked against the request: it is
// what ties the tab doing the approving to the terminal that asked.
export const cliAuthApproveSchema = z.object({ userCode: z.string().min(1).max(20) });
export type CliAuthApproveInput = z.infer<typeof cliAuthApproveSchema>;

// The polling half. `deviceSecret` never leaves the CLI process that started
// the request, so the id alone — which travels through a browser URL — can't
// collect the session.
export const cliAuthPollSchema = z.object({
  requestId: z.string().min(1),
  deviceSecret: z.string().min(1),
});
export type CliAuthPollInput = z.infer<typeof cliAuthPollSchema>;

// MFA (TOTP). `code` is a 6-digit TOTP for enable; for disable/login-verify it
// may also be a longer single-use recovery code (dashes/spaces allowed).
export const mfaEnableSchema = z.object({ code: z.string().min(6).max(10) });
export type MfaEnableInput = z.infer<typeof mfaEnableSchema>;

export const mfaDisableSchema = z.object({ code: z.string().min(6).max(40) });
export type MfaDisableInput = z.infer<typeof mfaDisableSchema>;

export const mfaVerifyLoginSchema = z.object({
  mfaToken: z.string().min(1),
  code: z.string().min(6).max(40),
});
export type MfaVerifyLoginInput = z.infer<typeof mfaVerifyLoginSchema>;

/** Must match apps/api/src/auth/oauth-provider.ts's OAUTH_PROVIDERS exactly — kept here too so frontend code (button lists, URL builders) has a single typed source instead of re-deriving/hardcoding the provider list per app. */
export const OAUTH_PROVIDERS = ['google', 'apple', 'microsoft', 'facebook'] as const;
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];
